/**
 * PullToRefresh - Pull-to-refresh gesture for installed PWA (standalone mode)
 *
 * The app disables native overscroll (`overscroll-behavior: none`), and in
 * standalone PWA mode there is no browser-provided pull-to-refresh at all.
 * This component re-implements the gesture: when the page is scrolled to the
 * top and the user pulls down past a threshold, it refetches all active
 * react-query queries and refreshes the Next.js router (server components).
 *
 * Only active in standalone display mode (installed PWA); does nothing in a
 * regular browser tab.
 */

"use client";

import { Box, Spinner } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { HiOutlineArrowDown } from "react-icons/hi2";
import { useThemeTokens } from "@/lib/theme/tokens";

/** Pull distance (after damping) required to trigger a refresh. */
const PULL_THRESHOLD = 70;
/** Maximum rendered pull distance. */
const MAX_PULL = 110;
/** Finger movement is damped so the indicator lags behind the finger. */
const DAMPING = 0.45;
/** Keep the spinner visible at least this long so the refresh feels real. */
const MIN_REFRESH_MS = 600;

function isStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function getMainScrollTop(): number {
  const container = document.querySelector(
    "[data-scroll-container]",
  ) as HTMLElement | null;
  if (container) return container.scrollTop;
  return window.scrollY || document.documentElement.scrollTop || 0;
}

/**
 * The gesture must not hijack scrolling inside modals, drawers, or any nested
 * scrollable element that is not itself at the top.
 */
function shouldIgnoreTouch(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('[role="dialog"], [role="alertdialog"], [role="menu"]')) {
    return true;
  }

  let el: HTMLElement | null =
    target instanceof HTMLElement ? target : target.parentElement;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    const isScrollable =
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight;
    if (isScrollable && !el.hasAttribute("data-scroll-container")) {
      // Nested scroll area (table, list, textarea...) handles its own scroll.
      if (el.scrollTop > 0) return true;
    }
    el = el.parentElement;
  }
  return false;
}

export const PullToRefresh = () => {
  const theme = useThemeTokens();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [enabled, setEnabled] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startYRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    setEnabled(isStandaloneDisplayMode());
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      startYRef.current = null;
      isPullingRef.current = false;
      setPullDistance(0);
    };

    const triggerRefresh = async () => {
      isRefreshingRef.current = true;
      setIsRefreshing(true);
      const startedAt = Date.now();
      try {
        router.refresh();
        await queryClient.refetchQueries({ type: "active" });
      } finally {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_REFRESH_MS) {
          await new Promise((resolve) =>
            setTimeout(resolve, MIN_REFRESH_MS - elapsed),
          );
        }
        isRefreshingRef.current = false;
        setIsRefreshing(false);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;
      if (e.touches.length !== 1) return;
      if (getMainScrollTop() > 0) return;
      if (shouldIgnoreTouch(e.target)) return;

      startYRef.current = e.touches[0].clientY;
      startXRef.current = e.touches[0].clientX;
      isPullingRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;

      const dy = e.touches[0].clientY - startYRef.current;
      const dx = e.touches[0].clientX - startXRef.current;

      if (!isPullingRef.current) {
        // Mostly-horizontal movement or upward scroll: not a pull gesture.
        if (dy <= 0 || Math.abs(dx) > dy) {
          startYRef.current = null;
          return;
        }
        if (getMainScrollTop() > 0) {
          startYRef.current = null;
          return;
        }
        isPullingRef.current = true;
      }

      if (dy > 0) {
        if (e.cancelable) e.preventDefault();
        setPullDistance(Math.min(dy * DAMPING, MAX_PULL));
      } else {
        reset();
      }
    };

    const onTouchEnd = () => {
      if (startYRef.current === null) return;
      const shouldRefresh =
        isPullingRef.current && !isRefreshingRef.current;

      setPullDistance((current) => {
        if (shouldRefresh && current >= PULL_THRESHOLD) {
          void triggerRefresh();
        }
        return 0;
      });
      startYRef.current = null;
      isPullingRef.current = false;
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, queryClient, router]);

  if (!enabled) return null;

  const isVisible = pullDistance > 0 || isRefreshing;
  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const indicatorOffset = isRefreshing
    ? 24
    : Math.max(pullDistance - 40, -40);

  return (
    <Box
      position="fixed"
      top="env(safe-area-inset-top, 0px)"
      left="50%"
      zIndex={1400}
      pointerEvents="none"
      opacity={isVisible ? 1 : 0}
      transform={`translateX(-50%) translateY(${indicatorOffset}px)`}
      transition={
        isRefreshing || pullDistance === 0
          ? "transform 0.25s ease-out, opacity 0.25s ease-out"
          : "none"
      }
    >
      <Box
        w="40px"
        h="40px"
        borderRadius="full"
        bg={theme.surfaceGlass.cardStrong}
        backdropFilter="blur(12px)"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        boxShadow={theme.shadow.card}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {isRefreshing ? (
          <Spinner size="sm" color={theme.accent.primary} thickness="2px" />
        ) : (
          <Box
            as={HiOutlineArrowDown}
            color={progress >= 1 ? theme.accent.primary : theme.text.tertiary}
            fontSize="18px"
            opacity={0.4 + progress * 0.6}
            transform={progress >= 1 ? "rotate(180deg)" : "rotate(0deg)"}
            transition="transform 0.2s ease-out, color 0.2s ease-out"
          />
        )}
      </Box>
    </Box>
  );
};
